/**
 * String 복사(Ctrl+드래그) — 격자에서 삭제된 Module 칸(localIndex) 계산
 * 전체 row×col 중 맵에 남은 solarModuleId가 없는 슬롯만 skip
 */
import { readModuleDisplayNo, readStringSeq } from './panelMapFeatureProps.js';

/**
 * @param {number} i row index [0, moduleRowNum)
 * @param {number} j col index [0, moduleColNum)
 */
export function gridLocalIndexFromCell(i, j, moduleRowNum, moduleColNum, orientation = 'row-major') {
  if (orientation === 'col-major') {
    return j * moduleRowNum + i + 1;
  }
  return i * moduleColNum + j + 1;
}

/**
 * @param {import('ol/Feature').default[]|object[]} features
 * @param {number|string} stringSeq
 * @returns {Set<string>}
 */
export function collectModuleDisplayIdsForString(features, stringSeq) {
  const key = String(stringSeq);
  const ids = new Set();
  (features || []).forEach(feature => {
    const get = feature?.get?.bind(feature);
    if (!get) {
      return;
    }
    if ((get('featureType') || '').toLowerCase() !== 'module') {
      return;
    }
    if (readStringSeq(feature) !== key) {
      return;
    }
    const displayNo = readModuleDisplayNo(feature);
    if (displayNo == null || displayNo === '') {
      return;
    }
    ids.add(String(displayNo));
    const num = Number(displayNo);
    if (Number.isFinite(num)) {
      ids.add(String(num));
    }
  });
  return ids;
}

function hasDisplayId(existingDisplayIds, localIndex) {
  return (
    existingDisplayIds.has(String(localIndex)) || existingDisplayIds.has(localIndex)
  );
}

/**
 * 격자 1..row×col 중 맵에 없는 localIndex = 삭제된 칸
 * @returns {Set<number>}
 */
export function computeDeletedGridSlots({
  moduleRowNum,
  moduleColNum,
  moduleNumberingOrientation = 'row-major',
  existingDisplayIds
}) {
  const skip = new Set();
  if (!moduleRowNum || !moduleColNum || moduleRowNum < 1 || moduleColNum < 1) {
    return skip;
  }
  if (!existingDisplayIds?.size) {
    for (let localIndex = 1; localIndex <= moduleRowNum * moduleColNum; localIndex++) {
      skip.add(localIndex);
    }
    return skip;
  }

  for (let i = 0; i < moduleRowNum; i++) {
    for (let j = 0; j < moduleColNum; j++) {
      const localIndex = gridLocalIndexFromCell(
        i,
        j,
        moduleRowNum,
        moduleColNum,
        moduleNumberingOrientation
      );
      if (!hasDisplayId(existingDisplayIds, localIndex)) {
        skip.add(localIndex);
      }
    }
  }
  return skip;
}

/**
 * Ctrl+드래그 복사용 — 원본 String feature + 격자 크기로 skip Set 반환
 * @param {object} params
 * @returns {Set<number>|null} skip 없으면 null (전체 격자 생성)
 */
export function resolveStringCopySkipGridSlots({
  sourceStringFeature,
  moduleRowNum,
  moduleColNum,
  moduleNumberingOrientation,
  mapFeatures
}) {
  if (!sourceStringFeature || !moduleRowNum || !moduleColNum) {
    return null;
  }
  const stringSeq = readStringSeq(sourceStringFeature);
  if (stringSeq == null) {
    return null;
  }

  const existingDisplayIds = collectModuleDisplayIdsForString(mapFeatures, stringSeq);
  const skip = computeDeletedGridSlots({
    moduleRowNum,
    moduleColNum,
    moduleNumberingOrientation,
    existingDisplayIds
  });

  return skip.size > 0 ? skip : null;
}
